import React, { useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Header } from "../../../components/header";
import { CardHeader } from "../../../components/cards";
import { SendIcon } from "../../../components/icons";
import { Loading } from "../../../components/loading";
import { Input, Select, Textarea } from "../../../components/form";
import { Button } from "../../../components/button";
import { useCustomerTask, useUpdateCustomerTask } from "../hooks";
import { useUsers } from "../../users/hooks";
import { CustomerTaskStatus } from "../../../__generated__/globalTypes";
import { customerTaskStatusLabel, CUSTOMER_TASK_PRIORITY_LABELS } from "../../../helpers/enum-labels";

type IUpdateCustomerTask = {
  id: string;
};

export const UpdateCustomerTask: React.FC = () => {
  const { id } = useParams<IUpdateCustomerTask>();
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) {
      navigate("/");
    }
  }, []);

  const { data, refetch } = useCustomerTask(+id!);
  const { data: usersData } = useUsers({ where: {}, limit: 100, offset: 0 });
  const { form, submit, loading } = useUpdateCustomerTask({
    id: +id!,
    onCompleted: () => {
      refetch();
      toast.success("La tâche a été modifiée avec succès");
    },
  });

  useEffect(() => {
    if (data?.customerTask?.ok && data?.customerTask?.result) {
      const { result } = data.customerTask;
      form.setValue("title", result.title);
      form.setValue("description", result.description);
      form.setValue("priority", result.priority);
      form.setValue("status", result.status);
      form.setValue("assignedToId", result.assignedTo?.id ?? null);
    }
  }, [data]);

  if (!data) {
    return <Loading />;
  }

  const task = data?.customerTask?.result;

  return (
    <>
      <Header
        title="Tâche"
        subtitle={task ? `#${task.id}` : ""}
        buttons={[
          {
            actionText: "Annuler",
            bgColor: "indigo",
            textColor: "white",
            link: `/customer-tasks`,
            icon: <SendIcon />,
          },
        ]}
      />
      <div className="main-container">
        {task?.customer && (
          <div className="card mb-4">
            <CardHeader title="Client" />
            <Link
              to={`/customer/${task.customer.id}`}
              className="text-primary-600 font-medium hover:underline"
            >
              {task.customer.name}
            </Link>
          </div>
        )}

        <div className="card">
          <CardHeader title="Modifier la tâche" />
          <div className="w-full">
            <div className="flex flex-wrap gap-3">
              <Input label="Titre" className="w-full" {...form.register("title")} />
              <Textarea label="Description" className="w-full" {...form.register("description")} />
              <Select label="Priorité" className="w-full" {...form.register("priority")}>
                {Object.entries(CUSTOMER_TASK_PRIORITY_LABELS).map(([value, { label }]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
              <Select label="Statut" className="w-full" {...form.register("status")}>
                {Object.keys(CustomerTaskStatus).map((key) => (
                  <option key={key} value={key}>
                    {customerTaskStatusLabel(key)}
                  </option>
                ))}
              </Select>
              <Select label="Assigné à" className="w-full" {...form.register("assignedToId")}>
                {usersData?.users?.results?.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.firstname} {user.lastname}
                  </option>
                ))}
              </Select>
            </div>
            <div className="w-full mt-4">
              <Button onClick={submit} loading={loading} disabled={loading}>
                Valider
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
